import * as React from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { ulid } from "ulid";
import { useToggle } from "react-use";
import { useEsgBoxScope } from "./esg-portal.api";
import { useGetLastConversationIdQuery, useCreateNewConversationMutation } from "@/modules/chat/chat.api";
import { useGetFileDetailsQuery } from "@/api/files";
import { ChatInterface } from "@/modules/chat/views/ChatInterface";
import { ArtefactsViewer } from "@/components/feature/artefactsViewer";
import { extractFilesFromFolder, findFirstFileId } from "@/modules/chat/chat.utils";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { Loader } from "@/components/ui/loader";
import { LeftSidebar } from "@/components/chat/LeftSidebar";
import { QAHistoryPanel } from "@/components/chat/QAHistoryPanel";
import { PromptLibraryContextProvider } from "@/modules/promptLibrary/promptLibrary.context";
import { ChatSidePanel } from "@/modules/chat/components/ChatSidePanel";
import { CreatePromptLibraryItemDialog } from "@/modules/chat/components/CreatePromptLibraryItemDialog";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";

const EsgChatContent: React.FC = () => {
  const { slug = "", conversationId = "" } = useParams<{ slug: string; conversationId: string }>();
  const navigate = useNavigate();
  const boxScope = useEsgBoxScope(slug);
  const scope = React.useMemo(() => (boxScope?.folderId ? { fseId: boxScope.folderId } : null), [boxScope?.folderId]);

  const [createConversation] = useCreateNewConversationMutation();
  const { data: lastConversationId } = useGetLastConversationIdQuery(scope?.fseId ?? "", {
    skip: !scope?.fseId || Boolean(conversationId),
    refetchOnMountOrArgChange: true,
  });

  const activeConversationId = conversationId || lastConversationId;

  const {
    data: chatContext,
    isLoading: isFolderLoading,
    isError: isFolderError,
  } = useGetFileDetailsQuery({ fse_id: scope?.fseId ?? "", depth: 200 }, { skip: !scope?.fseId });
  const [conversationError, setConversationError] = React.useState(false);

  const { setActiveTab, setRootItem, setCurrentPdfId, selectedFiles } = useCurrentPdf();

  const [promptGeneratorConversationId] = React.useState(() => ulid());
  const [isLeftPanelOpen, toggleIsLeftPanelOpen] = useToggle(true);

  React.useEffect(() => {
    setActiveTab("pdf");
  }, [setActiveTab]);

  React.useEffect(() => {
    if (chatContext) {
      setRootItem(chatContext);
      if (chatContext.type === "file") {
        setCurrentPdfId(chatContext.id);
      } else {
        const firstFileId = findFirstFileId(chatContext);
        if (firstFileId) setCurrentPdfId(firstFileId);
      }
    }
  }, [chatContext, setRootItem, setCurrentPdfId]);

  React.useEffect(() => {
    if (!conversationId && lastConversationId && scope?.fseId) {
      navigate(`/esg/${slug}/${lastConversationId}`, { replace: true });
    }
  }, [conversationId, lastConversationId, slug, navigate, scope?.fseId]);

  const isCreatingRef = React.useRef(false);
  const handleNewConversation = React.useCallback(async () => {
    if (!scope?.fseId || isCreatingRef.current) return;
    isCreatingRef.current = true;
    try {
      const newId = await createConversation(scope.fseId).unwrap();
      navigate(`/esg/${slug}/${newId}`);
    } catch {
      toast.error("Failed to create a new conversation. Please try again.");
      setConversationError(true);
    } finally {
      isCreatingRef.current = false;
    }
  }, [scope?.fseId, createConversation, navigate, slug]);

  React.useEffect(() => {
    if (scope?.fseId && !activeConversationId && !isFolderLoading) {
      handleNewConversation();
    }
  }, [scope?.fseId, activeConversationId, isFolderLoading, handleNewConversation]);

  const allFileIds = React.useMemo(
    () => (chatContext && chatContext.type === "folder" ? extractFilesFromFolder(chatContext).map(f => f.id) : []),
    [chatContext],
  );

  const excludeFileIds = React.useMemo(() => {
    if (!chatContext || chatContext.type !== "folder" || selectedFiles.length === 0) return [];
    const selectedSet = new Set(selectedFiles);
    return allFileIds.filter(id => !selectedSet.has(id));
  }, [chatContext, selectedFiles, allFileIds]);

  if (isFolderError || conversationError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-red-500">
          {isFolderError ? "Failed to load folder data." : "Failed to start a conversation."}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setConversationError(false);
            navigate(`/esg/${slug}`, { replace: true });
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!boxScope || !scope || isFolderLoading || !chatContext || !activeConversationId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  return (
    <PromptGeneratorContextProvider>
      <div className="flex h-full overflow-hidden">
        <LeftSidebar systemId={scope.fseId} isOpen={isLeftPanelOpen} onToggle={toggleIsLeftPanelOpen} />

        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2">
            <button
              onClick={() => navigate("/esg")}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>
            <span className="text-sm font-medium text-gray-700">{boxScope.label}</span>
          </div>
          <div className="mx-auto flex h-full w-[90%] max-w-[800px] flex-col pt-2">
            <ChatInterface
              fsEntry={chatContext}
              systemId={scope.fseId}
              key={activeConversationId}
              conversationId={activeConversationId}
              excludeFileIds={excludeFileIds}
            />
          </div>
        </div>

        <ChatSidePanel
          promptGeneratorConversationId={promptGeneratorConversationId}
          historyPanel={
            <QAHistoryPanel
              systemId={scope.fseId}
              currentConversationId={activeConversationId}
              basePath={`/esg/${slug}`}
            />
          }
          artefactsPanel={<ArtefactsViewer />}
        />
      </div>

      <CreatePromptLibraryItemDialog />
    </PromptGeneratorContextProvider>
  );
};

export const EsgChatView: React.FC = () => (
  <PromptLibraryContextProvider>
    <CurrentPdfProvider>
      <EsgChatContent />
    </CurrentPdfProvider>
  </PromptLibraryContextProvider>
);
